import React from 'react';
import { useLanguage } from '../context/LanguageContext';
import { schoolInfo, images } from '../data/mock';
import { Button } from './ui/button';
import { ChevronRight, MapPin, Users, Award } from 'lucide-react';

const HeroSection = () => {
  const { t } = useLanguage();

  return (
    <section className="relative min-h-screen flex items-center">
      {/* Background Image with Overlay */}
      <div className="absolute inset-0 z-0">
        <img
          src={images.hero}
          alt="Children ballet class"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#8B1C1C]/90 via-[#8B1C1C]/70 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        <div className="max-w-2xl">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 mb-6">
            <MapPin className="w-4 h-4 text-[#fad24b]" />
            <span className="text-white/90 text-sm font-medium">
              Surabaya, Indonesia
            </span>
          </div>

          {/* Main Heading */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-tight mb-6">
            {schoolInfo.name}
          </h1>

          {/* Tagline */}
          <p className="text-xl sm:text-2xl text-white/90 font-light mb-4">
            {t(schoolInfo.taglineEn, schoolInfo.taglineId)}
          </p>

          {/* Description */}
          <p className="text-lg text-white/80 mb-8 leading-relaxed">
            {t(
              `Professional ballet training for children aged ${schoolInfo.ageRange} years. Nurturing young talents with love and dedication.`,
              `Pelatihan balet profesional untuk anak usia ${schoolInfo.ageRange} tahun. Membina bakat muda dengan cinta dan dedikasi.`
            )}
          </p>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row gap-4 mb-12">
            <Button
              className="bg-white text-[#8B1C1C] hover:bg-[#f7f5f2] px-8 py-6 text-lg font-semibold rounded-full transition-all hover:scale-105"
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              {t('Enroll Now', 'Daftar Sekarang')}
              <ChevronRight className="w-5 h-5 ml-2" />
            </Button>
            <Button
              variant="outline"
              className="border-2 border-white text-white hover:bg-white/10 px-8 py-6 text-lg font-semibold rounded-full"
              onClick={() => document.getElementById('about').scrollIntoView({ behavior: 'smooth' })}
            >
              {t('Learn More', 'Pelajari Lebih')}
            </Button>
          </div>

          {/* Stats */}
          <div className="flex flex-wrap gap-8">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-[#fad24b]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{schoolInfo.studentsCount}</p>
                <p className="text-white/70 text-sm">{t('Students Trained', 'Siswa Terlatih')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-white/10 backdrop-blur-sm rounded-full flex items-center justify-center">
                <Award className="w-6 h-6 text-[#fad24b]" />
              </div>
              <div>
                <p className="text-2xl font-bold text-white">{schoolInfo.yearsExperience}</p>
                <p className="text-white/70 text-sm">{t('Years Experience', 'Tahun Pengalaman')}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll Indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-10">
        <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
          <div className="w-1.5 h-3 bg-white rounded-full animate-bounce" />
        </div>
      </div>
    </section>
  );
};

export default HeroSection;
